/**
 * The org's improvement projects switch (GET and PUT /v1/twin/settings/improvement-projects): off until an org admin
 * turns it on, and only behind the platform's gate (platform_enabled): while the platform has not opened projects to the
 * org the switch is not shown at all, since the server would refuse it (403 improvement_projects_not_enabled). Shown to
 * org admins only (Admin, and the Improvement projects page), and only where the live platform serves the route and the
 * org has the foundry (twin) pack on, so a platform without it, or an org outside the pack, shows nothing at all and
 * asks the server nothing. The pill is the platform console's own (OrgToggle).
 *
 * ImprovementProjectsSwitchView is pure (rendered in tests without providers); ImprovementProjectsSwitch holds the read
 * and the save.
 */
import { FolderKanban } from "lucide-react";
import { Card } from "@/lib/ui/card";
import { OrgToggle } from "@/routes/platform/org-toggle";
import { errorDetailOf } from "@/lib/api/error";
import { useEnabledPacksQuery } from "@/lib/api";
import { useImprovementProjectsSettingsQuery, useSaveImprovementProjectsSettings } from "@/lib/api/improvement-projects";
import { SWITCH_LEAD, SWITCH_TITLE, forbiddenText, switchStateText } from "@/lib/improvement-projects/words";
import { useOrg } from "@/lib/org-context";

export function ImprovementProjectsSwitchView({
  enabled,
  saving = false,
  error = null,
  onChange,
}: {
  enabled: boolean;
  saving?: boolean;
  error?: string | null;
  onChange?: (enabled: boolean) => void;
}) {
  return (
    <Card data-improvement-projects-switch={enabled ? "on" : "off"} className="flex flex-col gap-2 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-on-surface">
          <FolderKanban aria-hidden className="size-4 text-on-surface-muted" />
          {SWITCH_TITLE}
        </h2>
        <OrgToggle
          on={enabled}
          busy={saving}
          onClick={() => onChange?.(!enabled)}
          icon={FolderKanban}
          onTitle="Turn improvement projects off for this plant"
          offTitle="Turn improvement projects on for this plant"
        />
      </div>
      <p className="text-sm text-on-surface-muted">{SWITCH_LEAD}</p>
      <p className="text-xs text-on-surface-muted">{switchStateText(enabled)}</p>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </Card>
  );
}

/**
 * The switch for an org admin, where the platform serves it and has opened projects to the org; nothing for anyone
 * else, for an org outside the twin pack, while the platform's gate is shut, or before the read answers. The read waits on the pack, the way the sidebar's does (AppLayout's twinEnabled):
 * the route answers 403 to an org without the pack, so a non-foundry org's Admin page must not ask.
 */
export function ImprovementProjectsSwitch() {
  const org = useOrg();
  const packs = useEnabledPacksQuery();
  const twinEnabled = (packs.data?.packs ?? []).some((p) => p.pack === "foundry");
  const read = useImprovementProjectsSettingsQuery(twinEnabled);
  const save = useSaveImprovementProjectsSettings();
  const settings = read.data;
  if (!twinEnabled || !settings || !settings.platform_enabled || !org.can("org.admin")) return null;
  return (
    <ImprovementProjectsSwitchView
      enabled={settings.enabled}
      saving={save.isPending}
      error={save.isError ? (forbiddenText(save.error) ?? errorDetailOf(save.error) ?? "The switch could not be saved. Try again.") : null}
      onChange={(enabled) => save.mutate(enabled)}
    />
  );
}
